import {Injectable, inject} from "@angular/core";
import {Observable, filter, map} from "rxjs";
import {DataSnapshot, onValue, ref} from "firebase/database";
import {ThreePLServiceDetails} from "../models/threepl-service-details.model";
import {SplitService} from "../../../commons/services/split-service.service";
import {FirebaseService} from "../../../persistence-layer/firebase.service";
import {THREE_PL_SERVICES_CHANNEL} from "../constants/threepl-services.constant";

export type ThreePLServicePushAction = "created" | "changed" | "partialChanged";

export interface ThreePLServicePush {
  action: ThreePLServicePushAction;
  list: Partial<ThreePLServiceDetails>[];
}

@Injectable({providedIn: "root"})
export class ThreePLServicesChannelService {
  private readonly firebaseService = inject<FirebaseService>("firebaseService" as never);
  private readonly splitService = inject<SplitService>("splitService" as never);

  pushes(): Observable<ThreePLServicePush> {
    return new Observable<ThreePLServicePush>((subscriber) => {
      const {channel, database} = this.firebaseService;
      if (!channel || !database) {
        return undefined;
      }
      const branchIds = this.splitService.getUserSiteBranchIds() ?? [];
      const path = branchIds.length === 1
        ? `${channel.channelId}/branch/${branchIds[0]}/${THREE_PL_SERVICES_CHANNEL}`
        : `${channel.channelId}/${THREE_PL_SERVICES_CHANNEL}`;
      let initial = true;
      const unsubscribe = onValue(ref(database, path), (snapshot: DataSnapshot) => {
        if (initial) {
          initial = false;
          return;
        }
        const data = snapshot.val() as ThreePLServicePush | null;
        if (data && Array.isArray(data.list) && data.action) {
          subscriber.next(data);
        }
      });
      return () => unsubscribe();
    });
  }

  serviceUpdates(serviceId: string): Observable<{action: "changed" | "partialChanged"; entity: Partial<ThreePLServiceDetails>}> {
    return this.pushes().pipe(
      filter((push) => push.action === "changed" || push.action === "partialChanged"),
      map((push) => ({action: push.action as "changed" | "partialChanged", entity: push.list.find((e) => e?.id === serviceId)})),
      filter((update) => !!update.entity),
    );
  }
}

export function isNewerPush(current: Partial<ThreePLServiceDetails>, pushed: Partial<ThreePLServiceDetails>): boolean {
  const currentAt = current.updatedAt || current.updatedTime || current.analysisTime;
  const pushedAt = pushed.updatedAt || pushed.updatedTime || pushed.analysisTime;
  if (currentAt === undefined || pushedAt === undefined) {
    return true;
  }
  return currentAt !== pushedAt && new Date(pushedAt) > new Date(currentAt);
}

export function applyServicePush(current: ThreePLServiceDetails, action: "changed" | "partialChanged",
                                 pushed: Partial<ThreePLServiceDetails>): ThreePLServiceDetails {
  if (action === "changed") {
    return pushed as ThreePLServiceDetails;
  }
  return isNewerPush(current, pushed) ? {...current, ...pushed} : current;
}
